import { FormEvent, useState } from 'react'
import { Link, router, useForm, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { PasswordInput } from '@/shared/ui/password-input'
import { Label } from '@/shared/ui/label'
import { Checkbox } from '@/shared/ui/checkbox'
import { PhoneCodeSelect } from '@/shared/ui/phone-code-select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import type WebProfile from '@/shared/types/web/WebProfile'
import type WebProfileAccessCategories from '@/shared/types/web/WebProfileAccessCategories'
import { AccessCategoriesSection } from './AccessCategoriesSection'

interface ProfileEditProps {
  profile: WebProfile
  hasPin: boolean
  accessCategories: WebProfileAccessCategories
  canViewConfidentialInfo: boolean
  canEditConfidentialInfo: boolean
  [key: string]: unknown
}

interface ProfileFormData {
  firstName: string
  lastName: string
  middleName: string
  jobTitle: string
  phone: string
  phoneCode: string
  employeeId: string
}

interface PinFormData {
  newPin: string
  confirmPin: string
}

interface ConfidentialInfoFormData {
  canViewConfidentialInfo: boolean
}

const MIN_PIN_LENGTH = 4

type FormErrors = Record<string, string | string[] | undefined>

const FieldError = ({ errors, field }: { errors: FormErrors; field: string }) => {
  const fieldErrors = errors[field]
  const message = Array.isArray(fieldErrors) ? fieldErrors[0] : fieldErrors
  if (!message) return null
  return <p className="mt-1 text-sm text-destructive">{message}</p>
}

const ProfileEdit = () => {
  const { profile, hasPin, accessCategories, canViewConfidentialInfo, canEditConfidentialInfo } =
    usePage<ProfileEditProps>().props
  const canEditProfile = useCan(Resources.PROFILE, Actions.UPDATE)

  const profileForm = useForm<ProfileFormData>({
    firstName: profile.firstName ?? '',
    lastName: profile.lastName ?? '',
    middleName: profile.middleName ?? '',
    jobTitle: profile.jobTitle ?? '',
    phone: profile.phone ?? '',
    phoneCode: profile.phoneCode ?? '',
    employeeId: profile.employeeId ?? '',
  })

  const pinForm = useForm<PinFormData>({
    newPin: '',
    confirmPin: '',
  })

  const confidentialInfoForm = useForm<ConfidentialInfoFormData>({
    canViewConfidentialInfo,
  })

  const [showCancelDialog, setShowCancelDialog] = useState(false)

  const profileErrors: FormErrors = profileForm.errors as FormErrors
  const pinErrors: FormErrors = pinForm.errors as FormErrors

  const handleCancel = () => {
    if (profileForm.isDirty || pinForm.isDirty) {
      setShowCancelDialog(true)
    } else {
      router.visit('/profile')
    }
  }

  const handleProfileSubmit = (event: FormEvent) => {
    event.preventDefault()
    profileForm.transform((data) => ({
      profile: {
        first_name: data.firstName,
        last_name: data.lastName,
        middle_name: data.middleName,
        job_title: data.jobTitle,
        phone: data.phone,
        phone_code: data.phoneCode,
        employee_id: data.employeeId,
      },
    }))
    profileForm.patch('/profile')
  }

  const handlePinSubmit = (event: FormEvent) => {
    event.preventDefault()

    if (pinForm.data.newPin !== pinForm.data.confirmPin) {
      pinForm.setError('newPin', t('profile.pin.mismatch'))
      return
    }
    if (pinForm.data.newPin.length < MIN_PIN_LENGTH) {
      pinForm.setError('newPin', t('profile.pin.tooShort'))
      return
    }

    pinForm.transform((data) => ({
      pin: {
        new_pin: data.newPin,
        confirm_pin: data.confirmPin,
      },
    }))
    pinForm.patch('/profile/change_pin')
  }

  const handleConfidentialInfoSubmit = (event: FormEvent) => {
    event.preventDefault()
    confidentialInfoForm.patch('/profile/confidential_info')
  }

  const pinSectionTitle = hasPin
    ? t('profile.pin.sectionTitleChange')
    : t('profile.pin.sectionTitleSet')

  return (
    <div className="mx-auto max-w-3xl space-y-8 px-4 py-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('profile.editTitle')}</h1>
      </header>

      {/* Personal info form */}
      <section className="space-y-4 rounded-lg border p-6">
        <h2 className="text-lg font-semibold">{t('profile.personalInfo.sectionTitle')}</h2>

        {profileErrors.base?.length ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {profileErrors.base[0]}
          </div>
        ) : null}

        <form onSubmit={handleProfileSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="firstName">{t('profile.personalInfo.firstName')} *</Label>
              <Input
                id="firstName"
                placeholder={t('profile.personalInfo.firstNamePlaceholder')}
                value={profileForm.data.firstName}
                onChange={(event) => profileForm.setData('firstName', event.target.value)}
              />
              <FieldError errors={profileErrors} field="firstName" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lastName">{t('profile.personalInfo.lastName')} *</Label>
              <Input
                id="lastName"
                placeholder={t('profile.personalInfo.lastNamePlaceholder')}
                value={profileForm.data.lastName}
                onChange={(event) => profileForm.setData('lastName', event.target.value)}
              />
              <FieldError errors={profileErrors} field="lastName" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="middleName">{t('profile.personalInfo.middleName')}</Label>
            <Input
              id="middleName"
              placeholder={t('profile.personalInfo.middleNamePlaceholder')}
              value={profileForm.data.middleName}
              onChange={(event) => profileForm.setData('middleName', event.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">{t('profile.personalInfo.email')}</Label>
            <Input id="email" value={profile.email} readOnly className="bg-muted" />
            <p className="text-xs text-muted-foreground">
              {t('profile.personalInfo.emailReadOnly')}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="jobTitle">{t('profile.personalInfo.jobTitle')}</Label>
            <Input
              id="jobTitle"
              placeholder={t('profile.personalInfo.jobTitlePlaceholder')}
              value={profileForm.data.jobTitle}
              onChange={(event) => profileForm.setData('jobTitle', event.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="min-w-0 space-y-1.5">
              <Label>{t('profile.personalInfo.phoneCode')}</Label>
              <PhoneCodeSelect
                value={profileForm.data.phoneCode}
                onValueChange={(value) => profileForm.setData('phoneCode', value)}
              />
            </div>
            <div className="min-w-0 space-y-1.5">
              <Label htmlFor="phone">{t('profile.personalInfo.phone')}</Label>
              <Input
                id="phone"
                type="tel"
                placeholder={t('profile.personalInfo.phonePlaceholder')}
                value={profileForm.data.phone}
                onChange={(event) =>
                  profileForm.setData('phone', event.target.value.replace(/[^\d+\-\s().]/g, ''))
                }
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="employeeId">{t('profile.personalInfo.employeeId')}</Label>
            <Input
              id="employeeId"
              placeholder={t('profile.personalInfo.employeeIdPlaceholder')}
              value={profileForm.data.employeeId}
              onChange={(event) => profileForm.setData('employeeId', event.target.value)}
            />
          </div>

          {!canEditProfile && <NoAccessNotice />}

          {canEditProfile && (
            <div className="flex gap-3">
              <Button type="submit" disabled={profileForm.processing}>
                {profileForm.processing ? t('profile.submitting') : t('profile.saveButton')}
              </Button>
              <Button type="button" variant="outline" onClick={handleCancel}>
                {t('profile.cancel')}
              </Button>
            </div>
          )}
        </form>
      </section>

      {/* PIN form */}
      <section className="space-y-4 rounded-lg border p-6">
        <h2 className="text-lg font-semibold">{pinSectionTitle}</h2>

        <form onSubmit={handlePinSubmit} className="space-y-4 max-w-sm">
          <div className="space-y-1.5">
            <Label htmlFor="newPin">{t('profile.pin.newPin')}</Label>
            <PasswordInput
              id="newPin"
              autoComplete="new-password"
              value={pinForm.data.newPin}
              onChange={(event) => pinForm.setData('newPin', event.target.value)}
            />
            <FieldError errors={pinErrors} field="newPin" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirmPin">{t('profile.pin.confirmPin')}</Label>
            <PasswordInput
              id="confirmPin"
              autoComplete="new-password"
              value={pinForm.data.confirmPin}
              onChange={(event) => pinForm.setData('confirmPin', event.target.value)}
            />
            <FieldError errors={pinErrors} field="confirmPin" />
          </div>

          {canEditProfile ? (
            <Button type="submit" disabled={pinForm.processing}>
              {t('profile.pin.submitButton')}
            </Button>
          ) : (
            <NoAccessNotice />
          )}
        </form>
      </section>

      {/* Permissions (admin/supervisor only) */}
      {canEditConfidentialInfo && (
        <section className="space-y-4 rounded-lg border p-6">
          <h2 className="text-lg font-semibold">{t('profile.permissions.sectionTitle')}</h2>

          <form onSubmit={handleConfidentialInfoSubmit} className="space-y-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id="canViewConfidentialInfo"
                checked={confidentialInfoForm.data.canViewConfidentialInfo}
                onCheckedChange={(checked) =>
                  confidentialInfoForm.setData('canViewConfidentialInfo', checked === true)
                }
              />
              <Label htmlFor="canViewConfidentialInfo">
                {t('profile.permissions.canViewConfidentialLabel')}
              </Label>
            </div>

            <Button type="submit" disabled={confidentialInfoForm.processing}>
              {t('profile.permissions.saveButton')}
            </Button>
          </form>
        </section>
      )}

      <AccessCategoriesSection accessCategories={accessCategories} />

      <div className="flex gap-4">
        <Button asChild variant="link" className="px-0">
          <Link href="/notification_preference">{t('profile.notificationPreferencesLink')}</Link>
        </Button>
      </div>

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('profile.cancelDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('profile.cancelDialog.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('profile.cancelDialog.keep')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => router.visit('/profile')}
            >
              {t('profile.cancelDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default ProfileEdit
